import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet } from '../utils/apiClient';

const TRIAL_ACK_PREFIX = 'mp:trial-expired-ack:';

function buildTrialAckKey(profile) {
  const uid = profile?.uid || 'anonymous';
  const marker = profile?.trialEndDate || profile?.subscriptionEndDate || 'trial';
  return `${TRIAL_ACK_PREFIX}${uid}:${marker}`;
}

export function clearTrialInterstitialAcknowledgements() {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    const keysToRemove = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (typeof key === 'string' && key.startsWith(TRIAL_ACK_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => window.sessionStorage.removeItem(key));
  } catch (_) {
    // Ignore storage failures.
  }
}

export function markTrialInterstitialAcknowledged(profile) {
  if (typeof window === 'undefined' || !window.sessionStorage || !profile) return;
  try {
    window.sessionStorage.setItem(buildTrialAckKey(profile), '1');
  } catch (_) {
    // Ignore storage failures.
  }
}

export function hasAcknowledgedTrialInterstitial(profile) {
  if (typeof window === 'undefined' || !window.sessionStorage || !profile) return false;
  try {
    return window.sessionStorage.getItem(buildTrialAckKey(profile)) === '1';
  } catch (_) {
    return false;
  }
}

export function TrialExpiredInterstitial() {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await apiGet('/user/me');
        if (!cancelled) {
          setProfile(data);
        }
      } catch (err) {
        console.warn('Failed to load expired trial profile:', err);
        if (!cancelled) {
          toast.error('Unable to load your account status right now.');
          navigate('/MealPlan', { replace: true });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const isExpired = useMemo(() => (profile?.subscriptionTier || '').toLowerCase() === 'expired', [profile]);

  useEffect(() => {
    if (!loading && !isExpired) {
      navigate('/MealPlan', { replace: true });
    }
  }, [isExpired, loading, navigate]);

  const continuePath = location.state?.from && location.state.from !== '/trial-expired'
    ? location.state.from
    : '/MealPlan';

  const handleUpgrade = () => {
    markTrialInterstitialAcknowledged(profile);
    navigate('/plans', { replace: true, state: { source: 'trial-expired' } });
  };

  const handleContinueReadOnly = () => {
    markTrialInterstitialAcknowledged(profile);
    navigate(continuePath, { replace: true });
  };

  if (loading) {
    return <div className="min-h-screen bg-[#F8F7F5]" />;
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#E7EED9_0%,#F8F7F5_44%,#F2EFE8_100%)] px-6 py-10">
      <div className="mx-auto max-w-md rounded-[32px] border border-black/10 bg-white/95 p-6 shadow-[0_24px_80px_rgba(31,40,20,0.12)] backdrop-blur">
        <div className="mx-auto mb-8 flex h-20 w-20 items-center justify-center">
          <img src="/favicon.svg" alt="MealPlan Genie" className="h-14 w-14 object-contain" />
        </div>

        <div className="text-center">
          <p className="mb-3 text-sm font-semibold tracking-[0.08em] text-[#6D7368]">MealPlan Genie</p>
          <h1 className="mb-4 text-[34px] font-semibold leading-[1.05] text-[#1F2814]">
            Your free trial has ended
          </h1>
          <p className="mx-auto mb-6 max-w-[34ch] text-[16px] leading-7 text-[#5A6252]">
            Your recipes, meal plans and shopping lists are safe and yours to keep — export them any time. To continue adding, editing and generating, reactivate your subscription.
          </p>
        </div>

        <div className="mb-6 flex justify-center">
          <p className="inline-flex items-center gap-2 rounded-full bg-[#EEF4E6] px-3 py-1.5 text-sm font-medium text-[#446032]">
            <Sparkles className="h-4 w-4" />
            Upgrade anytime to restore full access
          </p>
        </div>

        <button
          onClick={handleUpgrade}
          className="mb-3 w-full rounded-full bg-[#AFBB8B] px-6 py-4 text-[17px] font-semibold text-[#1F2814] transition hover:bg-[#9FAA7B]"
        >
          Upgrade now
        </button>

        <button
          onClick={handleContinueReadOnly}
          className="w-full rounded-full border border-[#C9D1C2] bg-transparent px-6 py-4 text-[17px] font-medium text-[#1F2814] transition hover:bg-[#F8F7F5]"
        >
          Continue in read-only
        </button>
      </div>
    </div>
  );
}

export default TrialExpiredInterstitial;